import { Alert, AlertTitle, Box, Chip, IconButton, Typography } from "@mui/material"
import { AddOutlined, EditOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import { type FC, useState } from "react"

import { useAssignUserRole, useGetRoles, useGetUser } from "../hooks"

import { DialogType, UserRolesDialog } from "./modals"

import { Button } from "@/components/common/Button"
import type { Role, User } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const UserRoles: FC<{ user: User }> = ({ user }) => {
  const { userId } = user
  const [dialogOpened, setDialogOpened] = useState(false)
  const [dialogType, setDialogType] = useState<DialogType | null>(null)

  const { roles } = useGetRoles({})

  const { reexecuteQuery } = useGetUser(userId)

  const { update } = useAssignUserRole()

  const triggerNotification = useNotificationsStore((store) => store.enqueueNotification)

  const handleAddRolesClick = (dialogType: DialogType) => {
    setDialogType(dialogType)
    setDialogOpened(true)
  }

  const handleClose = () => {
    setDialogType(null)
    setDialogOpened(false)
  }

  const handleSubmit = async (rolesIds: string[]) => {
    const result = await update({ input: { userId, rolesIds } })

    if (result.error) {
      triggerNotification({
        type: NotificationType.Error,
        message: "Failed to updated User roles ",
      })
    } else {
      triggerNotification({
        type: NotificationType.Success,
        message: "User roles updated successfully",
      })
    }

    reexecuteQuery()
  }

  return (
    <Box className='flex flex-col gap-4 rounded-md border border-solid border-gray-300 p-6'>
      <Box className='flex items-center justify-between gap-3'>
        <Typography variant='h5' className='text-gray-800'>
          User Roles
        </Typography>

        {user?.roles && user.roles.length > 0 && (
          <IconButton color='primary' aria-label='edit roles' onClick={() => handleAddRolesClick(DialogType.EDIT)}>
            <EditOutlined />
          </IconButton>
        )}
      </Box>

      {user?.roles && user.roles.length > 0 && (
        <Box className='flex flex-wrap items-center gap-2'>
          {user.roles.map(({ roleId, name }) => (
            <Chip key={roleId} label={name} className='rounded-sm bg-primary-100' />
          ))}
        </Box>
      )}

      {!user?.roles?.length && user.isActive && (
        <Box>
          <Alert
            icon={<WarningOutlined color='primary' />}
            severity='info'
            variant='filled'
            classes={{
              message: "w-full flex flex-row justify-between",
            }}
          >
            <Box>
              <AlertTitle className='font-normal leading-7 text-gray-900'>Add User Roles</AlertTitle>
            </Box>

            <Box className='flex items-center'>
              <Button size='small' appearance='outlined' onClick={() => handleAddRolesClick(DialogType.ADD)}>
                <AddOutlined />
                User Roles
              </Button>
            </Box>
          </Alert>
        </Box>
      )}

      {!user.isActive && (
        <Box>
          <Alert
            icon={<WarningOutlined color='error' />}
            variant='standard'
            color='info'
            classes={{
              message: "w-full flex flex-row justify-between",
            }}
            sx={{ backgroundColor: "white" }}
          >
            <Box>
              <AlertTitle className='font-normal leading-7 text-gray-900'>
                Inactive users can not login anymore, and they no longer have their roles nor permissions available
              </AlertTitle>
              <Typography variant='body2' className='mt-2 text-gray-700'>
                Activate this person again if you would like to give their user permissions back
              </Typography>
            </Box>
          </Alert>
        </Box>
      )}

      {dialogType && (
        <UserRolesDialog
          rolesList={roles}
          userRoles={user.roles as Role[]}
          open={dialogOpened}
          onClose={handleClose}
          onSubmit={handleSubmit}
          dialogType={dialogType}
        />
      )}
    </Box>
  )
}

export default UserRoles
